import { useEffect, useState, type CSSProperties } from 'react'
import apiClient from '../api/client'
import { errorCode, errorMessage } from '../api/ApiError'
import { Lightbox } from './VisorDocumento'
import { isImage, isPdf, type PreviewResponse } from './tipoDeDocumento'

interface DocumentPreviewProps {
  documentId: string
  filename?: string
  compact?: boolean
}

// Vista vertical A4: proporción 1:1.414. Usamos aspectRatio para que el ancho
// mande y el alto crezca en consecuencia — evita que un PDF vertical se vea
// aplastado dentro de una caja horizontal fija.
const A4_ASPECT = '1 / 1.414'

export default function DocumentPreview({ documentId, filename = 'documento', compact = false }: DocumentPreviewProps) {
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  // El archivo se borró del almacén: no es una falla del portal y no se dice
  // igual. Antes el backend firmaba la URL igual y el `<iframe>` terminaba
  // mostrando el XML crudo de S3 —«NoSuchKeyThe specified key does not
  // exist…»— que no le dice nada al revisor.
  const [ausente, setAusente] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let alive = true
    setPreview(null); setError(null); setAusente(false)
    void apiClient.get<PreviewResponse>(`/documents/${encodeURIComponent(documentId)}/preview`)
      .then((response) => { if (alive) setPreview(response.data) })
      .catch((err: unknown) => {
        if (!alive) return
        if (errorCode(err) === 'archivo_ausente') setAusente(true)
        else setError(errorMessage(err, 'No se pudo cargar la vista previa.'))
      })
    return () => { alive = false }
  }, [documentId])

  const box: CSSProperties = {
    // aspectRatio hace que la caja siga la orientación vertical del documento;
    // el contenedor padre le da el ancho y el alto se ajusta solo.
    aspectRatio: A4_ASPECT,
    width: '100%',
    maxHeight: compact ? '320px' : '620px',
    borderRadius: '12px',
    overflow: 'hidden',
    position: 'relative',
    background: 'var(--c-surface-subtle)',
    border: '1px solid var(--c-surface-border)',
  }

  const expandBtnStyle: CSSProperties = {
    all: 'unset', cursor: 'pointer',
    position: 'absolute', top: '10px', right: '10px', zIndex: 2,
    background: 'rgb(13 21 32 / 0.75)', color: 'white',
    padding: '6px 12px', borderRadius: '8px',
    fontSize: '11.5px', fontWeight: 700,
    display: 'flex', alignItems: 'center', gap: '6px',
  }

  if (ausente) return (
    <div style={{ ...box, display: 'grid', placeItems: 'center', padding: '18px', textAlign: 'center' }}>
      <div>
        <div style={{ fontSize: '22px', lineHeight: 1, marginBottom: '8px' }}>🗎</div>
        <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--c-ink-soft)' }}>
          El archivo ya no está
        </div>
        <div style={{ fontSize: '11.5px', color: 'var(--c-ink-muted)', marginTop: '4px',
                      maxWidth: '26ch', marginInline: 'auto' }}>
          El registro del documento sigue, pero el original se borró del almacén.
        </div>
      </div>
    </div>
  )
  if (error) return (
    <div style={{ ...box, display: 'grid', placeItems: 'center', padding: '18px', textAlign: 'center' }}>
      <span style={{ fontSize: '12px', color: 'var(--c-ink-muted)' }}>{error}</span>
    </div>
  )
  if (!preview) return (
    <div style={{ ...box, display: 'grid', placeItems: 'center' }}>
      <span style={{ fontSize: '12px', color: 'var(--c-ink-muted)' }}>Preparando vista previa...</span>
    </div>
  )

  const displayName = preview.filename || filename
  const asImage = isImage(preview.mime_type, displayName)
  const asPdf = isPdf(preview.mime_type, displayName)

  return (
    <>
      <div style={box}>
        {(asImage || asPdf) && (
          <button onClick={() => setExpanded(true)} style={expandBtnStyle} title="Ampliar (Esc para cerrar)">
            <span style={{ fontSize: '13px', lineHeight: 1 }}>⤢</span> Ampliar
          </button>
        )}
        {asImage && (
          <img
            src={preview.url}
            alt={displayName}
            onClick={() => setExpanded(true)}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', cursor: 'zoom-in' }}
          />
        )}
        {asPdf && (
          <iframe
            title={`Vista previa: ${displayName}`}
            src={`${preview.url}#view=FitH&toolbar=0`}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: 'white' }}
          />
        )}
        {!asImage && !asPdf && (
          <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: '18px', textAlign: 'center' }}>
            <a href={preview.url} target="_blank" rel="noreferrer"
               style={{ color: 'var(--c-info-ink)', fontSize: '13px', fontWeight: 700 }}>
              Abrir {displayName}
            </a>
          </div>
        )}
      </div>
      {expanded && <Lightbox preview={preview} onClose={() => setExpanded(false)} />}
    </>
  )
}
